// =============================================================================
// SIREPPAR - Certificadores de Firmas
// =============================================================================
// Los certificadores son las personas que avalan las fichas de afiliación y las
// planillas de adherentes. NO son un órgano de conducción: son una función que
// alcanza a todo el partido.
//
// POR QUÉ SE MODELAN COMO ÓRGANO, IGUAL: `OrganoPartido.tipoOrgano` y
// `MandatoAutoridad.cargo` son strings libres (no enums de Prisma), así que un
// "órgano" CERTIFICADORES entra sin migración y hereda gratis todo lo que ya existe:
// la carga con validación de DNI contra el padrón, el gate que exige DNI frente y
// dorso verificados, el retiro de los promotores a la nómina histórica cuando se
// cargan las definitivas, y la edición desde /autoridades.
//
// Crear una tabla aparte habría obligado a duplicar las cinco cosas.
//
// LO QUE SÍ CAMBIA es el vocabulario: al partido se le habla de una "sección de
// Certificadores de Firmas", nunca de un órgano. De ahí `vocabularioOrgano()`, que
// los wizards usan para rotular el bloque en lugar de hardcodear "órgano".
//
// REGLAS PROPIAS de la sección (las aplica el render de los wizards y las revalida
// el backend en cargar-autoridades-definitivas):
//   - Cargo único: Certificador de Firmas. No hay otros cargos posibles.
//   - Todos TITULARES: no existe el certificador suplente.
//   - Entre 1 y 3 personas. El tope evita que se cargue media lista de afiliados.
// =============================================================================

export const TIPO_ORGANO_CERTIFICADORES = 'CERTIFICADORES';
export const CARGO_CERTIFICADOR_FIRMAS = 'CERTIFICADOR_FIRMAS';
export const LABEL_CERTIFICADORES = 'Certificadores de Firmas';
export const LABEL_CARGO_CERTIFICADOR = 'Certificador de Firmas';

/** Máximo de certificadores por partido. Mínimo: 1 (la sección es obligatoria). */
export const MAX_CERTIFICADORES = 3;

export const esSeccionCertificadores = (tipoOrgano: string): boolean =>
  tipoOrgano === TIPO_ORGANO_CERTIFICADORES;

/**
 * Cómo se le habla al partido de este bloque. Los certificadores no son un órgano,
 * así que el copy cambia; el resto de los órganos conserva el suyo.
 */
export interface VocabularioOrgano {
  /** Rótulo del campo de nombre. `null` = no se pide (la sección ya tiene nombre fijo). */
  labelNombre: string | null;
  /** Texto del botón que agrega una persona al bloque. */
  labelAgregarPersona: string;
  /** Cómo se cuentan las personas cargadas ("3 autoridad(es)" / "2 de 3 certificadores"). */
  contarPersonas: (n: number) => string;
  /** Ícono del encabezado del bloque. */
  icono: string;
}

export function vocabularioOrgano(tipoOrgano: string): VocabularioOrgano {
  if (esSeccionCertificadores(tipoOrgano)) {
    return {
      // La denominación es fija ("Certificadores de Firmas"): pedirle al partido que
      // le ponga nombre no tendría sentido, y el campo obligatorio lo trabaría.
      labelNombre: null,
      labelAgregarPersona: 'Agregar certificador',
      contarPersonas: (n) => `${n} de ${MAX_CERTIFICADORES} certificador(es)`,
      icono: '✍️',
    };
  }
  return {
    labelNombre: 'Nombre del órgano (según la carta orgánica) *',
    labelAgregarPersona: 'Agregar autoridad',
    contarPersonas: (n) => `${n} autoridad(es)`,
    icono: tipoOrgano === 'TRIBUNAL_CONDUCTA' ? '⚖️' : '📋',
  };
}
